import React, { useEffect, useMemo, useState } from 'react'
import { Bell } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { useAuthStore } from '@/store/auth.store'
import type { ApprovalStatusItem, InboxTaskListItem } from '@/api/types'
import { inboxTasksService } from '@/services/inboxTasksService'
import { formatRelativeTime } from '@/lib/format'

/**
 * Notifications bell component with dropdown menu
 * Shows unread count and allows managing notifications
 */
export const NotificationsBell: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0

  const [notifications, setNotifications] = useState<InboxTaskListItem[]>([])
  const [statuses, setStatuses] = useState<ApprovalStatusItem[]>([])

  const unreadCount = notifications.length

  const statusNameById = useMemo(() => {
    const map = new Map<number, string>()
    statuses.forEach((s) => {
      const name = s.lookupNameEn ?? s.lookupNameAr ?? ''
      if (s.id) {
        map.set(s.id, name)
      }
    })
    return map
  }, [statuses])

  const fetchNotifications = React.useCallback(async () => {
    if (!branchId) return
    try {
      const [statusList, notificationItems] = await Promise.all([
        inboxTasksService.getApprovalStatuses(),
        inboxTasksService.getNotifications(branchId),
      ])
      setStatuses(statusList || [])
      setNotifications(notificationItems || [])
    } catch (error) {
      console.error('Failed to load notifications:', error)
    }
  }, [branchId])

  // Load on mount and when branchId changes
  useEffect(() => {
    fetchNotifications()
  }, [fetchNotifications])

  // Refetch when user opens the dropdown so list is always up-to-date (no need to logout/login)
  const handleOpenChange = (open: boolean) => {
    if (open) {
      fetchNotifications()
    }
  }

  // Refetch when window regains focus (e.g. user returns from another tab) so badge count stays current
  useEffect(() => {
    const onFocus = () => {
      if (branchId) fetchNotifications()
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [branchId, fetchNotifications])

  const recentNotifications = notifications.slice(0, 5)

  return (
    <DropdownMenu onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-8 w-8 p-0">
          <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
            <Bell className="h-3 w-3 text-white" />
          </div>
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
          <span className="sr-only">Notifications</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80" align="end">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {notifications.length > 0 && (
            <div className="flex space-x-1">
              {/* Cross/Clear-all removed as requested */}
            </div>
          )}
        </DropdownMenuLabel>
        
        <DropdownMenuSeparator />
        
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <Bell className="h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              No notifications
            </p>
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            {recentNotifications.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className="flex flex-col items-start p-3 cursor-pointer"
                onClick={() => {
                  navigate(`/approval-jobs/${notification.id}`)
                }}
              >
                <div className="flex items-start justify-between w-full">
                  <div className="flex-1">
                    <p className="text-sm font-medium mb-1">
                      {notification.recordName}
                    </p>
                    <p className="text-xs text-muted-foreground mb-2">
                      <Badge variant="secondary" className="text-[10px] mr-2">
                        {statusNameById.get(notification.approvalStatusId) ?? notification.approvalStatusId}
                      </Badge>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {notification.createdDate ? formatRelativeTime(notification.createdDate) : ''}
                    </p>
                  </div>
                </div>
              </DropdownMenuItem>
            ))}
            
            {notifications.length > 5 && (
              <div className="p-2 text-center">
                <p className="text-xs text-muted-foreground">
                  {notifications.length - 5} more notifications
                </p>
              </div>
            )}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
